
import { defineComponent, ref, watch } from 'vue';

// Time for the opening/closing transition. Must be the same as in the SCSS styles.
const TRANSITION_TIME = 300;

export default defineComponent({
    props: {
        title: {
            type: String,
            required: true,
        },
        isOpen: {
            type: Boolean,
            default: false,
        }
    },

    emits: [
        "update:isOpen",
    ],

    setup(props, {emit})
    {
        const isCurrentlyOpen = ref(props.isOpen);
        const isHardOpen = ref(props.isOpen); // "HardOpen" means triggered by user, should not be closed on focusOut
        const isOpening = ref(false);
        const isClosing = ref(false);
        const collapsibleBody = ref<InstanceType<typeof HTMLElement>>();
        const collapsibleBodyContent = ref<InstanceType<typeof HTMLElement>>();
        const collapsiebleElement = ref<InstanceType<typeof HTMLElement>>();

        function setBodyContainerHeight(): void
        {
            if (collapsibleBody.value) {
                const targetHeight = collapsibleBodyContent.value?.getBoundingClientRect().height;
                collapsibleBody.value.style.height = `${targetHeight}px`;
            }
        }

        function clearBodyContainerHeight(): void
        {
            if (collapsibleBody.value) {
                collapsibleBody.value.style.height = "";
            }
        }

        function open(): void
        {
            // If the collapsible is already transitioning, do nothing
            if (isOpening.value || isClosing.value || isCurrentlyOpen.value) {
                return;
            }

            // When opening the collapsible, we need to first set the "opening" class to setup the transition
            isCurrentlyOpen.value = true;
            isOpening.value = true;
            
            // Then we can force the "body container" height to the target height of the content.
            // Because the transition was setup, the browser will animate it smoothly.
            setBodyContainerHeight();

            // Once the transition is finished, remove the "opening" class and the fixed height
            setTimeout(() => {
                isOpening.value = false;
                clearBodyContainerHeight();

                // Report that the state has changed
                emit("update:isOpen", isCurrentlyOpen.value);
            }, TRANSITION_TIME);

        }

        function close(): void
        {
            // If the collapsible is already transitioning, do nothing
            if (isOpening.value || isClosing.value || !isCurrentlyOpen.value) {
                return;
            }

            // When closing the collapsible, we need to first force the "body container" height, so the browser
            // can know from what value to transition from.
            setBodyContainerHeight();

            // Then we can set the "closing" class which will setup the transition.
            // We need to wait for the change of setBodyContainerHeight() to be applied before setting the class,
            // this can be done by deffering the execution of 0ms.
            setTimeout(() => {
                isCurrentlyOpen.value = false;
                isClosing.value = true;
            }, 0);

            // Once the transition is finished, remove the "closing" class and the fixed height
            setTimeout(() => {
                clearBodyContainerHeight();
                isClosing.value = false;

                // Report that the state has changed
                emit("update:isOpen", isCurrentlyOpen.value);
            }, TRANSITION_TIME);
        }

        function toggleCollapse(): void
        {
            if (isCurrentlyOpen.value) {
                close();
                isHardOpen.value = false;
            }
            else {
                open();
                isHardOpen.value = true;
            }
        }

        function handleFocus(): void
        {
            //await Async.timeout(0); // Ensure that if the user clicks, the click is handled before the focus event
            open();
        }

        function handleFocusOut(e: FocusEvent): void
        {
            // Never close a hard-open tab
            if (isHardOpen.value) {
                return;
            }

            // e.relatedTarget being null means that an other element of the browser was focused, meaning
            // focus is not within the collapsible anymore
            if (!e.relatedTarget || !(e.relatedTarget instanceof Element)) {
                return close();
            }

            // Closes the collapsible when the focus leaves
            if (!collapsiebleElement.value?.contains(e.relatedTarget)) {
                close();
            }
        }

        // Toggle the collapsible if the source property is modified
        watch(
            () => props.isOpen,
            (newValue) => {
                if (newValue) {
                    open();
                }
                else {
                    close();
                }
            }
        );

        return {
            collapsibleBody,
            collapsibleBodyContent,
            collapsiebleElement,
            isCurrentlyOpen,
            isOpening,
            isClosing,
            toggleCollapse,
            handleFocus,
            handleFocusOut,
        };
    }
});

